import type { ApiResponse } from '@abaninja/types';

import { httpClient } from '@abaninja/util-http-client';
import { createCancellablePromise } from '@abaninja/util-store';

import type { Attachment, Comment, Document, Payment, PaymentMethod, Transaction } from '../@types';

export function getDocuments() {
  return createCancellablePromise(
    ({ signal }) => httpClient.get<ApiResponse<Document[]>>('/customer-portal/v2/documents', { signal }),
  );
}

export function getDocumentPaymentMethods(uuid: string) {
  return createCancellablePromise(
    ({ signal }) => httpClient.get<ApiResponse<PaymentMethod[]>>(
      `/customer-portal/v2/documents/${uuid}/payment-methods`,
      { signal },
    ),
  );
}

export function getDocumentAttachments(uuid: string) {
  return createCancellablePromise(
    ({ signal }) => httpClient.get<ApiResponse<Attachment[]>>(`/customer-portal/v2/documents/${uuid}/attachments`, { signal }),
  );
}

export function getDocumentDownloadPath(uuid: string, pdfUuid: string) {
  return createCancellablePromise(
    ({ signal }) => httpClient.post<ApiResponse<Pick<Document['details'], 'pdf'>>>(
      `/customer-portal/v2/documents/${uuid}/actions/download`,
      { pdfUuid },
      { signal },
    ),
  );
}

export function getDocumentComments(uuid: string) {
  return createCancellablePromise(
    ({ signal }) => httpClient.get<ApiResponse<Comment[]>>(
      `/customer-portal/v2/documents/${uuid}/comments`,
      { signal },
    ),
  );
}

export function addComment(uuid: string, data: { comment: string }) {
  return httpClient.post<ApiResponse<Comment>>(`/customer-portal/v2/documents/${uuid}/actions/comment`, data);
}

export function getPayments(uuid: string) {
  return createCancellablePromise(
    ({ signal }) => httpClient.get<ApiResponse<{ payments: Payment[]; pendingTransactions: Transaction[] }>>(
      `/customer-portal/v2/documents/${uuid}/payments`,
      { signal },
    ),
  );
}

export function pay(uuid: string, data: { paymentMethod: Omit<PaymentMethod['type'], 'bank'> }) {
  return httpClient.post<ApiResponse<{ url: string }>>(`/customer-portal/v2/documents/${uuid}/actions/pay`, data);
}

export function approveQuote(uuid: string) {
  return httpClient.patch<never>(`/customer-portal/v2/documents/${uuid}/actions/approve`);
}

export function declineQuote(uuid: string) {
  return httpClient.patch<never>(`/customer-portal/v2/documents/${uuid}/actions/decline`);
}
